import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Box } from '@mui/material';
import { styled, alpha } from '@mui/material/styles';

interface ResizeHandleProps {
    onResize: (width: number) => void;
    initialWidth: number;
    minWidth?: number; // Default 24px - allows content to show ellipsis when very narrow
    maxWidth?: number;
}

const StyledResizeHandle = styled(Box)(({ theme }) => ({
    position: 'absolute',
    top: 0,
    right: -4, // Center it on the border
    bottom: 0,
    width: 6, // Make it wider for easier interaction
    cursor: 'col-resize',
    backgroundColor: theme.palette.mode === 'light' ? alpha(theme.palette.grey[500], 0.2) : alpha(theme.palette.grey[500], 0.3),
    transition: 'all 0.2s ease',
    zIndex: 10, // Ensure it's above other content
    height: '80%', // Only cover the middle 50% of the cell height to avoid interfering with content
    margin: 'auto 0', // Center vertically

    '.resize-handle-arrows': {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        opacity: 0,
        color: theme.palette.grey[700],
        fontSize: 10,
        pointerEvents: 'none',
        transition: 'opacity 0.2s ease',
        zIndex: 2,
    },

    // Make the entire area interactive
    '&::after': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'transparent',
    },

    // Add a visible indicator line
    '&::before': {
        content: '""',
        position: 'absolute',
        top: '10%',
        bottom: '10%',
        left: '50%',
        width: '1px',
        backgroundColor: 'transparent',
        transform: 'translateX(-50%)',
        transition: 'background-color 0.2s ease',
    },

    '&:hover': {
        backgroundColor: alpha(theme.palette.grey[500], 0.3), // Light gray background on hover

        '.resize-handle-arrows': {
            opacity: 1,
        },

        '&::before': {
            backgroundColor: theme.palette.grey[500],
            width: '2px',
        },
    },

    '&.resizing': {
        backgroundColor: alpha(theme.palette.grey[600], 0.35),

        '.resize-handle-arrows': {
            opacity: 1,
            color: theme.palette.grey[800],
        },

        '&::before': {
            backgroundColor: theme.palette.grey[600],
            width: '2px',
        },

        // Add a visual indicator that resizing is active across the entire viewport
        '&::after': {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'transparent',
            cursor: 'col-resize',
            zIndex: 9999,
            content: '""',
        },
    },

    // Ensure it's always interactive
    pointerEvents: 'auto',
}));

export const ResizeHandle: React.FC<ResizeHandleProps> = ({
    onResize,
    initialWidth,
    minWidth = 100, // Minimum width to show ellipsis properly
    maxWidth = 800,
}) => {
    const [isResizing, setIsResizing] = useState(false);
    const [startX, setStartX] = useState(0);
    const [startWidth, setStartWidth] = useState(initialWidth);
    const resizeHandleRef = useRef<HTMLDivElement>(null);
    const [currentWidth, setCurrentWidth] = useState(initialWidth);

    // Update current width when initialWidth changes
    useEffect(() => {
        setCurrentWidth(initialWidth);
    }, [initialWidth]);

    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        setIsResizing(true);
        setStartX(e.clientX);
        setStartWidth(currentWidth);

        // Add a flag to body to prevent other interactions
        document.body.style.userSelect = 'none';
    }, [currentWidth]);

    const handleMouseMove = useCallback((e: MouseEvent) => {
        if (!isResizing) return;

        e.preventDefault();
        e.stopPropagation();

        // Simple calculation without complex scroll adjustment
        const diff = e.clientX - startX;
        const newWidth = Math.max(minWidth, Math.min(maxWidth, startWidth + diff));
        console.log('Resizing to width:', newWidth);
        setCurrentWidth(newWidth);
        onResize(newWidth);
    }, [isResizing, startX, startWidth, minWidth, maxWidth, onResize]);

    const handleMouseUp = useCallback((e?: MouseEvent) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }

        setIsResizing(false);

        // Restore body styles
        document.body.style.userSelect = '';
    }, []);    // Handle mouse move and up events globally when resizing
    useEffect(() => {
        if (isResizing) {
            const handleGlobalMouseMove = (e: MouseEvent) => {
                handleMouseMove(e);
            };

            const handleGlobalMouseUp = (e: MouseEvent) => {
                handleMouseUp(e);
            };

            // Use passive listeners to improve performance
            document.addEventListener('mousemove', handleGlobalMouseMove, { capture: true, passive: false });
            document.addEventListener('mouseup', handleGlobalMouseUp, { capture: true, passive: false });

            // Also listen for mouse leave to handle cases where mouse leaves the window
            document.addEventListener('mouseleave', handleMouseUp, { passive: true });

            return () => {
                document.removeEventListener('mousemove', handleGlobalMouseMove, { capture: true });
                document.removeEventListener('mouseup', handleGlobalMouseUp, { capture: true });
                document.removeEventListener('mouseleave', handleMouseUp);
            };
        }
    }, [isResizing, handleMouseMove, handleMouseUp]);

    // Remove the separate useEffect for body styles since we handle it in mousedown/mouseup

    return (
        <StyledResizeHandle
            ref={resizeHandleRef}
            className={isResizing ? 'resizing' : ''}
            onMouseDown={handleMouseDown}
            role="separator"
            aria-label="Resize column"
            tabIndex={-1}
            data-resize-handle="true"
       />
    );
};

export default ResizeHandle;
